import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLang, pick } from "@/contexts/LanguageContext";
import { routeDetailPath, pathFor, ROUTE_KEYS } from "@/i18n/routeMap";

function routeMainImage(r) {
  const media = (r.routeMedia || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
  const main = media.find((m) => m.isMain) || media[0];
  if (main) return main.posterUrl || main.url;
  if (Array.isArray(r.images) && r.images.length) {
    const f = r.images[0];
    return typeof f === "string" ? f : f.url;
  }
  return "";
}

export default function RoutesPreview({ routes }) {
  const { lang, t } = useLang();
  const items = (routes || []).slice(0, 3);

  return (
    <section className="bg-[#0C0C0C] py-20 md:py-32 border-t border-[#2A2A2A]" data-testid="routes-preview">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-end justify-between gap-6 mb-12">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-px bg-[#C9A55A]" />
              <span className="text-overline text-[#C9A55A]">{t.routes.kicker}</span>
            </div>
            <h2 className="font-display text-[#F5F1EB] text-3xl md:text-5xl max-w-2xl">{t.routes.title}</h2>
          </div>
          <Link to={pathFor(ROUTE_KEYS.ROUTES, lang)}
            className="hidden md:inline-flex items-center gap-2 text-[#C9A55A] text-overline hover:underline"
            data-testid="routes-see-all">
            {t.routes.explore} <ArrowUpRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {items.map((r, idx) => {
            const slug = pick(r.slug, lang);
            const title = pick(r.title, lang);
            const image = routeMainImage(r);
            return (
              <motion.div
                key={r.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}>
                <Link to={routeDetailPath(slug, lang)} className="group block" data-testid={`route-preview-${slug}`}>
                  <div className="relative aspect-[4/5] overflow-hidden bg-[#2A2A2A]">
                    <img src={image} alt={title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0C0C0C] via-[#0C0C0C]/40 to-transparent" />
                    <div className="absolute bottom-0 inset-x-0 p-6">
                      <div className="text-overline text-[#C9A55A] mb-2">{String(idx + 1).padStart(2, "0")}</div>
                      <h3 className="font-display text-[#F5F1EB] text-2xl leading-tight">{title}</h3>
                      <div className="mt-4 flex items-center gap-2 text-[#C9A55A] text-xs tracking-[0.18em] uppercase font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                        {t.routes.explore} <ArrowUpRight size={14} />
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
